import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Background,
  BackgroundVariant,
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useReactFlow,
  useViewport,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { GripHorizontal, Move } from "lucide-react";
import type { StoryboardProject, StoryBriefState, AdBriefState, BuildNumbers } from "./types";
import BuildProgress, { type BuildUnit } from "./BuildProgress";
import {
  useStoryboardProject, useStoryBrief, useStudioProgress, useStudioResult, useClipReview,
  useAdBrief, useStudioStills,
  requestReviewSelect, requestReviewApprove, requestReviewReroll, requestReviewReplace,
  requestReviewUseAllClean, requestReviewAssemble,
  type StudioProgress, type StudioResult, type StudioClipReview, type StudioService, type StudioStills,
} from "./studioBus";
import { useAuthMedia, useAuthMediaUrl } from "@/lib/authMedia";
import StoryboardTimeline from "./StoryboardTimeline";
import StoryBriefCard from "./StoryBriefCard";
import AdBriefCard from "./AdBriefCard";
import MobileReviewSheet from "./MobileReviewSheet";
import MediaZoomViewer from "./MediaZoomViewer";
import { clipKindLabel, qcFlagged } from "./clipKind";
import { useBoardHand } from "./boardHand";
import ReasonChips, { useReasonPicks } from "./ReasonChips";
import { corridoApi } from "@/lib/corrido-api";
import { useCorridoLook } from "@/components/landing/CorridoLookBar";

// StoryboardCanvas — the workspace, and the approval gate for every render.
//
// Built on xyflow (React Flow) — the same library CapCut's AI creator canvas
// uses — so pan, zoom, node dragging and selection behave the way they do there
// instead of being approximated. Replaces a hand-rolled absolute-position drag
// surface that had no pan and no zoom.
//
// Nodes are cards on a dotted board:
//   • story brief  — the planned board awaiting approval (the thing to act on)
//   • ad brief     — the planned commercial awaiting approval (AdBriefCard)
//   • stills       — a finished image-ad grid with a download link per picture
//   • live project — the interactive storyboard timeline for a running job
//   • progress     — a job in flight (CapCut .loadingCard: 376×200, spinner, %)
//   • player       — the finished video (CapCut plays results ON the canvas)
//   • archived     — finished/replaced runs, dimmed, so past work never vanishes
//
// 376px is CapCut's module width; NODE_GAP lays cards out on that grid.

const NODE_W = 376;
const NODE_GAP = 32;
/** Was 1.5×, which stopped well short of a readable face on a 36-scene board
 *  (owner report 2026-10-05). Each card also opens a deep-zoom viewer. */
export const CANVAS_MAX_ZOOM = 4;
export const CANVAS_MIN_ZOOM = 0.2;

/** The hand's cursor and touch rules (boardHand sets the data attributes).
 *  touch-action: none on the whole board in hand mode: a finger anywhere on it
 *  drags the board, so the browser must not scroll the page. What the browser
 *  did for a finger there, the hand does: a strip of pictures (data-board-strip)
 *  scrolls under a sideways swipe, a slider slides (review, 2026-10-09: on a
 *  touch tablet the review's clips past the 9th could not be reached). */
const BOARD_CSS = `
.studio-board[data-tool="hand"] .react-flow__pane,
.studio-board[data-tool="hand"] .react-flow__node { cursor: grab; }
.studio-board[data-tool="hand"] .react-flow,
.studio-board[data-tool="hand"] .react-flow * { touch-action: none; }
.studio-board .react-flow__node img { -webkit-touch-callout: none; -webkit-user-drag: none; }
.studio-board[data-board-space] .react-flow,
.studio-board[data-board-space] .react-flow * { cursor: grab !important; }
.studio-board[data-board-panning] .react-flow,
.studio-board[data-board-panning] .react-flow * { cursor: grabbing !important; }
`;

/** The one place a card moves from (React Flow's dragHandle on every node). */
const GRIP_SELECTOR = ".studio-node-grip";

/** Node chrome — CapCut .nodeContainer: radius 20, 1px border, soft shadow.
 *  (The old ⤢/⤡ enlarge toggle was removed 2026-07-21: it only scaled the card
 *  without un-minimizing anything, which read as broken. Cards are full-size.) */
function NodeShell({
  title, dimmed, children,
}: {
  title: string;
  dimmed?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ opacity: dimmed ? 0.55 : 1, filter: dimmed ? "saturate(.6)" : "none", transition: "opacity .2s ease" }}>
      {/* The grip bar is the card's drag handle and the ONLY place a card moves
          from (dragHandle): a press anywhere else on the card moves the whole
          board (useBoardHand). It says so on itself ("Move card"), because a
          bar showing just the title read as a heading. Finger-sized (44px);
          touch-action/user-select stop the browser treating a press as a
          scroll, text selection or long-press menu before the drag starts. */}
      <div
        className="studio-node-grip" data-testid="studio-node-grip"
        title={t("studio.canvas.gripLabel")} aria-label={t("studio.canvas.gripLabel")}
        style={{
          alignItems: "center", background: "rgba(148,163,184,.08)", border: "1px solid #223046",
          borderRadius: 12, boxSizing: "border-box", color: "#94a3b8", cursor: "grab", display: "flex",
          fontSize: 13, fontWeight: 800, gap: 8, letterSpacing: 2, marginBottom: 6, minHeight: 44,
          padding: "0 12px", textTransform: "uppercase", touchAction: "none", userSelect: "none",
          WebkitTouchCallout: "none", WebkitUserSelect: "none", whiteSpace: "nowrap",
        }}
      >
        <GripHorizontal aria-hidden size={18} style={{ flexShrink: 0, opacity: .7 }} />
        {/* First, so a phone showing only the card's left edge still shows it. */}
        <span
          data-testid="studio-node-grip-move"
          style={{ alignItems: "center", border: "1px solid #2a3648", borderRadius: 8, color: "#7fb8e8", display: "inline-flex", flexShrink: 0, fontSize: 11, gap: 5, letterSpacing: 1, padding: "4px 8px" }}
        >
          <Move aria-hidden size={14} />
          {t("studio.canvas.gripMove")}
        </span>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{title}</span>
      </div>
      <div style={{ background: "#0e141d", border: "1px solid #223046", borderRadius: 20, boxShadow: "0 10px 24px rgba(0,0,0,.35)", overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );
}

function BriefNode({ data }: NodeProps) {
  const { t } = useTranslation();
  const brief = (data as { brief: StoryBriefState }).brief;
  return (
    <NodeShell title={t("studio.canvas.briefTitle")}>
      <StoryBriefCard brief={brief} />
    </NodeShell>
  );
}

function AdBriefNode({ data }: NodeProps) {
  const { t } = useTranslation();
  const brief = (data as { brief: AdBriefState }).brief;
  return (
    <NodeShell title={t("studio.canvas.adBriefTitle")}>
      <AdBriefCard brief={brief} />
    </NodeShell>
  );
}

/** Image ads as a canvas node — a grid of the finished stills, each one a
 *  download link. URLs are public storage URLs, so no auth fetch is needed. */
function StillsNode({ data }: NodeProps) {
  const { t } = useTranslation();
  const s = (data as { stills: StudioStills }).stills;
  return (
    <NodeShell title={t("studio.canvas.finishedSuffix", { title: s.title })}>
      <div className="nodrag" data-testid="studio-stills-node" style={{ boxSizing: "border-box", width: NODE_W, padding: 12 }}>
        <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(2, 1fr)" }}>
          {s.items.map((item) => (
            <a key={`${item.index}-${item.url}`} href={item.url} download target="_blank" rel="noreferrer" data-testid={`studio-still-${item.index}`}
              title={t("studio.canvas.download")}
              style={{ background: "#000", border: "1px solid #223046", borderRadius: 10, display: "block", overflow: "hidden", position: "relative" }}>
              <img src={item.url} alt="" loading="lazy" draggable={false} style={{ aspectRatio: "1 / 1", display: "block", objectFit: "cover", width: "100%" }} />
              <span style={{ background: "rgba(0,0,0,.72)", borderRadius: 4, color: "#fff", fontSize: 10, fontWeight: 700, left: 4, padding: "1px 5px", position: "absolute", top: 4 }}>{item.aspect}</span>
              <span style={{ background: "rgba(0,0,0,.72)", borderRadius: 4, color: "#fff", fontSize: 10, fontWeight: 700, bottom: 4, padding: "1px 5px", position: "absolute", right: 4 }}>↓ {t("studio.canvas.download")}</span>
            </a>
          ))}
        </div>
      </div>
    </NodeShell>
  );
}

export function ProjectNode({ data }: NodeProps) {
  const { project, dimmed, title } = data as unknown as { project: StoryboardProject; dimmed: boolean; title: string };
  const build = project.build ?? null;
  return (
    <NodeShell title={title} dimmed={dimmed}>
      {/* The storyboard being drawn carries the same moving bar as the chat,
          from the same frame — the board is never just placeholder tiles. */}
      {project.phase === "working" && !dimmed && (
        <div className="nodrag" style={{ borderBottom: "1px solid #223046", boxSizing: "border-box", padding: "14px 16px 12px", width: NODE_W * 2 }}>
          <BuildProgress
            jobKey={build?.key || "pending:working"}
            numbers={build?.numbers ?? null}
            receivedAt={build?.receivedAt ?? 0}
            label={build?.label ?? null}
            unit={build?.unit ?? "pictures"}
            testId="studio-board-build-progress"
          />
        </div>
      )}
      {/* The timeline keeps its clicks (a scene picture selects it) and its strip
          keeps the wheel (it scrolls sideways); a drag on it moves the board. */}
      <div className="nodrag" style={{ width: NODE_W * 2, pointerEvents: dimmed ? "none" : "auto" }}>
        <StoryboardTimeline project={project} />
      </div>
    </NodeShell>
  );
}

/** CapCut .loadingCard — 376×200, padding 12, a breathing inner panel with a
 *  spinner, the live pipeline message, and the progress %. */
function ProgressNode({ data }: NodeProps) {
  const { t } = useTranslation();
  const p = (data as { progress: StudioProgress }).progress;
  // A corrido build publishes its live numbers: the card draws the shared bar
  // (moving percent, counts, time left, elapsed) instead of a bare number.
  if (p.build) {
    return (
      <NodeShell title={p.title}>
        <div className="nodrag" style={{ boxSizing: "border-box", minHeight: 200, padding: 12, width: NODE_W }}>
          <div style={{ background: "rgba(255,255,255,.04)", borderRadius: 16, boxSizing: "border-box", minHeight: 176, padding: "16px 16px" }}>
            <BuildProgress
              jobKey={p.build.key}
              numbers={p.build.numbers}
              receivedAt={p.build.receivedAt}
              label={p.build.label || p.message || null}
              unit={p.build.unit ?? null}
              testId="studio-progress-build"
            />
          </div>
        </div>
      </NodeShell>
    );
  }
  return (
    <NodeShell title={p.title}>
      <div className="nodrag" style={{ boxSizing: "border-box", width: NODE_W, height: 200, padding: 12 }}>
        <div style={{
          alignItems: "center", background: "rgba(255,255,255,.04)", borderRadius: 16,
          display: "flex", flexDirection: "column", gap: 10, height: "100%",
          justifyContent: "center", padding: "0 20px",
          animation: "ldc-breathe 1s ease-in-out infinite alternate",
        }}>
          <div style={{
            width: 24, height: 24, borderRadius: "50%",
            border: "2px solid rgba(255,255,255,.15)", borderTopColor: "#38bdf8",
            animation: "ldc-rotate 1s linear infinite",
          }} />
          <div style={{ color: "#b6c2d4", fontSize: 12, lineHeight: 1.5, maxHeight: 54, overflow: "hidden", textAlign: "center" }}>
            {p.message || t("studio.canvas.working")}
          </div>
          {p.progress !== null && (
            <div style={{ alignItems: "center", display: "flex", gap: 8, width: "100%" }}>
              <div style={{ background: "rgba(255,255,255,.08)", borderRadius: 3, flex: 1, height: 4, overflow: "hidden" }}>
                <div style={{ background: "linear-gradient(90deg,#38bdf8,#38bdf8)", height: "100%", transition: "width .6s ease", width: `${Math.max(2, p.progress)}%` }} />
              </div>
              <span style={{ color: "#7c8aa0", fontSize: 13, fontVariantNumeric: "tabular-nums", width: 34, textAlign: "right" }}>
                {Math.round(p.progress)}%
              </span>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes ldc-rotate{to{transform:rotate(360deg)}}
        @keyframes ldc-breathe{from{opacity:.75}to{opacity:1}}
      `}</style>
    </NodeShell>
  );
}

/** The finished video as a canvas node — CapCut's player module is 376 wide. */
export function PlayerNode({ data }: NodeProps) {
  const { t } = useTranslation();
  const r = (data as { result: StudioResult }).result;
  const { url, failed: fetchFailed, retry: refetch } = useAuthMedia(r.url);
  // A fetch or a decode that fails says so, instead of "Loading" forever.
  const [unplayable, setUnplayable] = useState(false);
  useEffect(() => setUnplayable(false), [url]);
  const failed = fetchFailed || unplayable;
  const retry = () => { setUnplayable(false); if (fetchFailed) refetch(); };
  return (
    <NodeShell title={`${r.title} · finished`}>
      <div className="nodrag" style={{ boxSizing: "border-box", width: NODE_W, padding: 12 }}>
        {failed ? (
          <div data-testid="studio-player-failed" style={{ alignItems: "center", color: "#fca5a5", display: "flex", flexDirection: "column", fontSize: 12, gap: 10, height: 200, justifyContent: "center" }}>
            {t("studio.canvas.playerFailed")}
            <button type="button" onClick={retry} style={{ background: "transparent", border: "1px solid #3f3f46", borderRadius: 8, color: "#e4e4e7", cursor: "pointer", fontSize: 12, padding: "6px 12px" }}>
              {t("studio.canvas.playerRetry")}
            </button>
          </div>
        ) : url ? (
          <video
            src={url}
            controls
            playsInline
            onError={() => setUnplayable(true)}
            style={{ background: "#000", borderRadius: 12, display: "block", maxHeight: 480, width: "100%" }}
          />
        ) : (
          <div style={{ alignItems: "center", color: "#7c8aa0", display: "flex", fontSize: 12, height: 200, justifyContent: "center" }}> Loading your video…
          </div>
        )}
      </div>
    </NodeShell>
  );
}

/** STAGE-2 CLIP REVIEW on the board — watch each animated clip, re-roll/replace,
 *  approve, then assemble. All of it happens here now instead of the chat, so a
 *  resumed run is edited on the board (user directive 2026-07-21). */
function ReviewThumb({ path }: { path: string | null }) {
  const url = useAuthMediaUrl(path || "");
  return (
    <div style={{ width: 46, height: 82, background: "#000", borderRadius: 6, overflow: "hidden" }}>
      {url && <img src={url} alt="" loading="lazy" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
    </div>
  );
}
export function ClipsReviewNode({ data }: NodeProps) {
  const review = (data as { review: StudioClipReview }).review;
  const clips = review.clips;
  const { t } = useTranslation();
  const sel = clips.find((c) => c.idx === review.selectedIdx) || clips[0] || null;
  const selVideo = useAuthMediaUrl(sel?.videoUrl || "");
  const [steer, setSteer] = useState("");
  // "What's wrong?" chips for the clip on show: cleared on another clip and
  // once a new take or a new scene is asked for.
  const [reasons, setReasons, clearReasons] = useReasonPicks(sel?.idx ?? null);
  const [viewerIdx, setViewerIdx] = useState<number | null>(null);
  const openViewer = (idx: number) => setViewerIdx(clips.findIndex((c) => c.idx === idx));
  const approvedSet = new Set(review.approved);
  const busy = review.busy || review.rerolling !== null;
  const flaggedCount = clips.filter((c) => qcFlagged(c.qc)).length;
  const btn = (bg: string, bd: string, col: string): React.CSSProperties => ({
    background: bg, border: `1px solid ${bd}`, borderRadius: 8, color: col, cursor: "pointer",
    fontSize: 12, fontWeight: 700, padding: "6px 10px",
  });
  return (
    <NodeShell title={t("studio.canvas.reviewTitle", { count: clips.length })}>
      <div className="nodrag" style={{ boxSizing: "border-box", width: NODE_W * 1.4, padding: 14 }}>
        <div style={{ alignItems: "flex-start", display: "flex", flexWrap: "wrap", gap: 12 }}>
          {sel && (
            <div style={{ flexShrink: 0, position: "relative", width: 190 }}>
              {selVideo
                ? <video key={sel.videoUrl} src={selVideo} controls playsInline loop style={{ aspectRatio: "9 / 16", background: "#000", borderRadius: 10, display: "block", width: "100%" }} />
                : <div style={{ aspectRatio: "9 / 16", background: "#000", borderRadius: 10, width: "100%" }} />}
              <button type="button" onClick={() => openViewer(sel.idx)} data-testid="review-zoom"
                aria-label={t("studio.zoom.open")} title={t("studio.zoom.open")}
                style={{ background: "rgba(0,0,0,.72)", border: "1px solid #2a3648", borderRadius: 999, color: "#fff", cursor: "pointer", fontSize: 15, height: 34, position: "absolute", right: 6, top: 6, width: 34 }}>⤢</button>
            </div>
          )}
          <div style={{ display: "grid", flex: 1, gap: 8, minWidth: 180 }}>
            {sel && (
              <>
                <div style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 700 }}>
                  {t("studio.canvas.clipOf", { n: clips.findIndex((c) => c.idx === sel.idx) + 1, total: clips.length })}
                  <span data-testid="review-kind" style={{ color: "#7c8aa0", fontWeight: 400 }}> · {clipKindLabel(t, sel.kind)}</span>
                </div>
                {qcFlagged(sel.qc) && (
                  <div data-testid="review-qc" style={{ color: "#e3b341", fontSize: 12, lineHeight: 1.5 }}>{t("studio.canvas.qcFlagged", { problems: (sel.qc?.problems || []).join("; ") })}</div>
                )}
                {sel.theme && <div style={{ color: "#94a3b8", fontSize: 12 }}>{sel.theme}</div>}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  <button type="button" onClick={() => requestReviewApprove(sel.idx)}
                    style={btn(approvedSet.has(sel.idx) ? "rgba(52,211,153,.14)" : "transparent", approvedSet.has(sel.idx) ? "#34d399" : "#2a3648", approvedSet.has(sel.idx) ? "#34d399" : "#e2e8f0")}>
                    {approvedSet.has(sel.idx) ? t("studio.canvas.inTheVideo") : t("studio.canvas.useThisClip")}
                  </button>
                  <button type="button" disabled={busy || review.rerollsLeft <= 0} data-testid="review-newtake"
                    onClick={() => { requestReviewReroll({ idx: sel.idx, reasons }); clearReasons(); }}
                    style={{ ...btn("rgba(56,189,248,.1)", "#38bdf8", "#fff"), opacity: busy || review.rerollsLeft <= 0 ? 0.5 : 1 }}>
                    {review.rerolling === sel.idx ? t("studio.canvas.rendering") : t("studio.canvas.newTake")}
                  </button>
                </div>
                <ReasonChips kind="clips" value={reasons} onChange={setReasons} disabled={busy || review.rerollsLeft <= 0} testId="review-reasons" />
                <div style={{ display: "flex", gap: 6 }}>
                  <input value={steer} onChange={(e) => setSteer(e.target.value)} placeholder={t("studio.canvas.steerPlaceholder")}
                    style={{ background: "#0a0e15", border: "1px solid #2a3648", borderRadius: 8, color: "#e2e8f0", flex: 1, fontSize: 12, minWidth: 0, padding: "6px 9px" }} />
                  <button type="button" disabled={busy || review.rerollsLeft <= 0} data-testid="review-replace"
                    onClick={() => { requestReviewReplace({ idx: sel.idx, scene: steer.trim() || undefined, reasons }); setSteer(""); clearReasons(); }}
                    style={{ ...btn("rgba(56,189,248,.08)", "#38bdf8", "#fff"), opacity: busy || review.rerollsLeft <= 0 ? 0.5 : 1, whiteSpace: "nowrap" }}>
                    {t("studio.canvas.replaceScene")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        {/* A new take in flight (10-15 min): the same moving bar as the chat. */}
        {review.rerolling !== null && review.build && (
          <BuildProgress
            jobKey={review.build.key}
            numbers={review.build.numbers}
            receivedAt={review.build.receivedAt}
            label={review.build.label ?? null}
            unit={review.build.unit ?? "clips"}
            compact
            testId="review-build-progress"
            style={{ marginTop: 10 }}
          />
        )}
        {/* strip — every thumbnail is the clip's own still (`image` from the
            engine); a quality-check flag is a visible badge, not only a border. */}
        {flaggedCount > 0 && (
          <div data-testid="review-qc-count" style={{ color: "#e3b341", fontSize: 11, marginTop: 8 }}>{t("studio.canvas.qcCount", { count: flaggedCount })}</div>
        )}
        {/* A thumbnail is a picture to hold as well as a button: a drag on it
            moves the board (data-board-grab), a click picks the clip. A finger
            swiping sideways scrolls the strip (data-board-strip): the phone's
            review sheet shows only on a phone held upright, so on a tablet this
            is the one way to every clip. */}
        <div data-board-strip data-testid="review-strip" style={{ display: "flex", gap: 6, marginTop: 10, overflowX: "auto", paddingBottom: 6 }}>
          {clips.map((c, pos) => (
            <button key={c.idx} type="button" onClick={() => requestReviewSelect(c.idx)} onDoubleClick={() => openViewer(c.idx)}
              data-board-grab data-testid={`review-thumb-${c.idx}`} data-qc={qcFlagged(c.qc) ? "flagged" : "ok"}
              style={{ background: "#0a0e15", border: `2px solid ${sel?.idx === c.idx ? "#38bdf8" : qcFlagged(c.qc) ? "#e3b341" : "#2a3648"}`, borderRadius: 8, cursor: "pointer", flexShrink: 0, overflow: "hidden", padding: 0, position: "relative" }}>
              <ReviewThumb path={c.thumbUrl} />
              <span style={{ background: "rgba(0,0,0,.72)", borderRadius: 4, color: "#fff", fontSize: 11, fontWeight: 700, left: 3, padding: "1px 5px", position: "absolute", top: 3 }}>{pos + 1}</span>
              {approvedSet.has(c.idx) && <span style={{ background: "rgba(52,211,153,.9)", borderRadius: 4, color: "#04140b", fontSize: 11, fontWeight: 800, padding: "1px 4px", position: "absolute", right: 3, top: 3 }}>✓</span>}
              {qcFlagged(c.qc) && <span style={{ background: "rgba(227,179,65,.95)", borderRadius: 4, bottom: 3, color: "#1a1200", fontSize: 9, fontWeight: 800, left: 3, letterSpacing: 0.5, padding: "1px 4px", position: "absolute", textTransform: "uppercase" }}>{t("studio.canvas.qcBadge")}</span>}
            </button>
          ))}
        </div>
        <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
          <span style={{ color: "#7c8aa0", fontSize: 11 }}>{t("studio.canvas.rerollsLeft", { count: review.rerollsLeft })}</span>
          <button type="button" onClick={() => requestReviewUseAllClean()} style={btn("rgba(52,211,153,.1)", "#34d399", "#34d399")}>{t("studio.canvas.useAllClean")}</button>
          <button type="button" disabled={review.approved.length < 4 || busy} onClick={() => requestReviewAssemble()}
            style={{ ...btn(review.approved.length >= 4 && !busy ? "#38bdf8" : "rgba(255,255,255,.08)", "transparent", review.approved.length >= 4 && !busy ? "#04121e" : "#7c8aa0"), fontWeight: 800, letterSpacing: 1 }}
            title={review.approved.length < 4 ? t("studio.canvas.pickAtLeastFour") : undefined}>
            {t("studio.canvas.buildMyVideo", { count: review.approved.length })}
          </button>
        </div>
      </div>
      {viewerIdx !== null && viewerIdx >= 0 && (
        <MediaZoomViewer
          items={clips.map((c, i) => ({ src: c.videoUrl || c.thumbUrl, kind: c.videoUrl ? "video" : "image", label: c.theme || t("studio.canvas.clipOf", { n: i + 1, total: clips.length }) }))}
          index={Math.min(viewerIdx, clips.length - 1)}
          onIndex={setViewerIdx}
          onClose={() => setViewerIdx(null)}
        />
      )}
    </NodeShell>
  );
}

/** An UNFINISHED / past project parked on the board (moved here out of the
 *  ChopShop). Field report 2026-07-21: these showed as tiny "minimized" chips —
 *  the user wants them as FULL, selectable storyboards (like CapCut's multiple
 *  video cards). So the card is full width, NOT dimmed, shows a real thumbnail
 *  strip of its clips, and the WHOLE card opens the storyboard. */
type UnfinishedRun = BuildNumbers & {
  jobId: string; type: string; status: string; run_id: string | null; created_at: string; thumbs?: string[];
};
/** A status frame as the card reads it (the build numbers plus the run id). */
type RunStatus = BuildNumbers & { run_id?: string | null };

const RESUME_POLL_MS = 4000;
const isRunInFlight = (status: string | null | undefined) => status === "processing" || status === "queued";
/** What a run type produces, for the count line when the frame names no op. */
function unitForRunType(type: string): BuildUnit | null {
  if (type === "corrido_storyboard" || type === "corrido_regen") return "pictures";
  if (type === "corrido_clips" || type === "corrido_reroll") return "clips";
  return null;
}

const corridoImg = (p: string) => `/api/web/corrido/image?path=${encodeURIComponent(p)}`;

/** One still in a past-run storyboard strip (auth-gated media). */
function ResumeThumb({ path }: { path: string }) {
  const url = useAuthMediaUrl(corridoImg(path));
  return (
    <div style={{ flex: "0 0 auto", width: 44, height: 78, borderRadius: 6, overflow: "hidden", background: "#0a0e15", border: "1px solid #223046" }}>
      {url && <img src={url} alt="" loading="lazy" draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
    </div>
  );
}

export function ResumeNode({ data }: NodeProps) {
  const { t } = useTranslation();
  const { run, onDelete, onRefresh } = data as unknown as {
    run: UnfinishedRun; onDelete?: (jobId: string) => void; onRefresh?: () => void;
  };
  // A build still running is followed LIVE on its card (owner report
  // 2026-09-28: the card said "still building" with no bar and no way in).
  // It polls /status every few seconds while mounted, draws the shared bar,
  // and flips to the finished label — asking the board to reload its runs —
  // the moment the job ends.
  const startedInFlight = isRunInFlight(run.status);
  const [live, setLive] = useState<{ s: RunStatus; at: number } | null>(null);
  const [ended, setEnded] = useState<string | null>(null);
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;
  useEffect(() => {
    if (!startedInFlight) return;
    let stopped = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    const token = localStorage.getItem("auth_token");
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    const jid = encodeURIComponent(run.jobId);
    const tick = async () => {
      try {
        const r = await fetch(`${corridoApi("/status")}?id=${jid}&job_id=${jid}`, { headers });
        if (stopped) return;
        if (!r.ok) {
          // Gone, not ours, or signed out: asking again every few seconds for
          // as long as the board is open will never change the answer.
          if (r.status === 401 || r.status === 403 || r.status === 404) {
            stopped = true;
            if (timer) clearInterval(timer);
            // A job that no longer exists is not building: no live bar for it.
            if (r.status === 404) setEnded("failed");
          }
          return;
        }
        const s = (await r.json()) as RunStatus;
        if (stopped) return;
        setLive({ s, at: Date.now() });
        if (s.status === "completed" || s.status === "failed" || s.status === "cancelled") {
          stopped = true;
          if (timer) clearInterval(timer);
          setEnded(s.status);
          refreshRef.current?.();
        }
      } catch { /* keep the last frame; the next tick tries again */ }
    };
    void tick();
    timer = setInterval(tick, RESUME_POLL_MS);
    return () => { stopped = true; if (timer) clearInterval(timer); };
  }, [startedInFlight, run.jobId]);

  const inFlight = startedInFlight && !ended;
  const status = ended || run.status;
  const runId = (live?.s.run_id as string | null | undefined) || run.run_id;
  const label = inFlight
    ? (run.type === "corrido_storyboard" ? t("studio.canvas.stillBuildingBoard")
      : run.type === "corrido_clips" || run.type === "corrido_reroll" ? t("studio.canvas.stillFilming")
      : run.type === "corrido_regen" ? t("studio.canvas.stillRedrawing")
      : t("studio.canvas.stillAssembling"))
    : status === "failed" || status === "cancelled" ? t("studio.canvas.interrupted")
    : run.type === "corrido_storyboard" || run.type === "corrido_regen" ? t("studio.canvas.boardReady")
    : run.type === "corrido_assemble" || run.type === "corrido_revise" ? t("studio.canvas.finishedVideo")
    : t("studio.canvas.clipsReady");
  // While it runs, the card opens the LIVE build (the chat reconnects to this
  // job and shows the same bar); once it has a run, it opens that run.
  const href = inFlight
    ? `/?resume_corrido_job=${encodeURIComponent(run.jobId)}#director-console`
    : runId ? `/?resume_corrido=${encodeURIComponent(runId)}#director-console` : undefined;
  const thumbs = run.thumbs || [];
  // Seed from the /runs row (a server may send its progress fields there too)
  // until the first poll answers.
  const numbers: BuildNumbers = live?.s ?? run;
  const Inner = (
    <div className="nodrag" style={{ boxSizing: "border-box", padding: 14, width: NODE_W }}>
      <div style={{ alignItems: "center", display: "flex", gap: 8, justifyContent: "space-between" }}>
        <div data-testid="resume-label" style={{ color: "#e2e8f0", fontSize: 15, fontWeight: 700 }}>{label}</div>
        <div style={{ alignItems: "center", display: "flex", gap: 6, flexShrink: 0 }}>
          {inFlight && (
            <span style={{ background: "rgba(56,189,248,.14)", border: "1px solid #38bdf8", borderRadius: 999, color: "#7fb8e8", fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "2px 8px" }}>{t("studio.canvas.renderingBadge")}</span>
          )}
          {/* DELETE — remove this storyboard from the board (user directive
              2026-07-22). Blocked while a render is in flight so a customer
              can't wipe work that's actively costing GPU. */}
          {onDelete && !inFlight && (
            <button
              type="button" title={t("studio.canvas.deleteStoryboard")}
              onClick={(e) => {
                e.preventDefault(); e.stopPropagation();
                if (window.confirm(t("studio.canvas.deleteConfirm"))) onDelete(run.jobId);
              }}
              style={{ background: "transparent", border: "1px solid #3a2530", color: "#f87171", borderRadius: 7, width: 26, height: 26, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
            >✕</button>
          )}
        </div>
      </div>
      {inFlight && (
        <BuildProgress
          jobKey={run.jobId}
          numbers={numbers}
          receivedAt={live?.at ?? 0}
          unit={unitForRunType(run.type)}
          compact
          testId="resume-build-progress"
          style={{ marginTop: 10 }}
        />
      )}
      {!inFlight && (
        <div style={{ color: "#7c8aa0", fontSize: 12, marginTop: 6 }}>
          {t("studio.canvas.projectKeepsWorking", { id: String(runId || run.jobId).slice(0, 8) })}
        </div>
      )}
      {/* FULL storyboard strip — the whole point: this reads as a storyboard, not a chip. */}
      {thumbs.length > 0 && (
        // A finger swiping sideways scrolls it (data-board-strip); any other swipe moves the board.
        <div data-board-strip data-testid="resume-strip" style={{ display: "flex", gap: 5, marginTop: 10, overflowX: "auto", paddingBottom: 4 }}>
          {thumbs.slice(0, 12).map((p, i) => <ResumeThumb key={i} path={p} />)}
          {thumbs.length > 12 && <span style={{ alignSelf: "center", color: "#7c8aa0", fontSize: 11, paddingLeft: 4 }}>+{thumbs.length - 12}</span>}
        </div>
      )}
      {href && (
        <span
          data-testid="resume-open"
          style={{ alignItems: "center", background: "rgba(56,189,248,.14)", border: "1px solid #38bdf8", borderRadius: 10, color: "#e2e8f0", display: "inline-flex", fontSize: 13, fontWeight: 700, marginTop: 12, padding: "8px 14px" }}
        >
          {inFlight ? t("studio.canvas.openLiveBuild") : t("studio.canvas.openAndReview")}
        </span>
      )}
    </div>
  );
  return (
    <NodeShell title={t("studio.canvas.resumeTitle", { date: new Date(run.created_at).toLocaleDateString() })}>
      {href
        ? <a href={href} data-testid="resume-link" style={{ color: "inherit", display: "block", textDecoration: "none" }}>{Inner}</a>
        : Inner}
    </NodeShell>
  );
}

/** Per-tool empty state. The story copy ("nothing is charged until you press
 *  Generate on the card") is true for the TikTok story; the corrido board has
 *  its own money rule — free storyboard, priced animation — and says so; the
 *  ads board says its own (the plan is free, Generate charges). */
function EmptyState({ activeService }: { activeService: StudioService | null }) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="studio-canvas-empty" data-service={activeService || "none"} style={{ background: "rgba(5,7,11,.75)", border: "1px dashed #223046", borderRadius: 14, left: 24, maxWidth: "calc(100% - 48px)", padding: "20px 22px", position: "absolute", top: 24, width: 520, zIndex: 5 }}
    >
      <div style={{ color: "hsl(var(--primary))", fontSize: 12, fontWeight: 800, letterSpacing: 2, textTransform: "uppercase" }}>{t("studio.canvas.emptyTitle")}
      </div>
      {activeService === "corrido" ? (
        <>
          <p style={{ color: "#b6c2d4", fontSize: 13, lineHeight: 1.6, marginTop: 10 }}>{t("studio.canvas.emptyCorridoBody1")}</p>
          <p style={{ color: "#b6c2d4", fontSize: 13, lineHeight: 1.6, marginTop: 8 }}>{t("studio.canvas.emptyCorridoBody2")}</p>
        </>
      ) : activeService === "ads" ? (
        <>
          <p style={{ color: "#b6c2d4", fontSize: 13, lineHeight: 1.6, marginTop: 10 }}>{t("studio.canvas.emptyAdsBody1")}</p>
          <p style={{ color: "#b6c2d4", fontSize: 13, lineHeight: 1.6, marginTop: 8 }}>{t("studio.canvas.emptyAdsBody2")}</p>
        </>
      ) : (
        <>
          <p style={{ color: "#b6c2d4", fontSize: 13, lineHeight: 1.6, marginTop: 10 }}>{t("studio.canvas.emptyBody1")}
            <b> {t("studio.canvas.emptyPlan")}</b> {t("studio.canvas.emptyBody1Tail")}
          </p>
          <p style={{ color: "#b6c2d4", fontSize: 13, lineHeight: 1.6, marginTop: 8 }}>{t("studio.canvas.emptyBody2")} <b>{t("studio.canvas.emptyBody2Bold")}</b> {t("studio.canvas.emptyBody2Tail")}
          </p>
        </>
      )}
    </div>
  );
}

/** CapCut's canvas toolbar (.container-lLy0v6 + .zoomSection): a 44px blurred
 *  pill pinned bottom-left with a live zoom % readout. Their +/- live in the
 *  dropdown; ours are one click because that's strictly faster. */
function CanvasToolbar({ tool, setTool }: { tool: "hand" | "select"; setTool: (t: "hand" | "select") => void }) {
  const { t } = useTranslation();
  const { zoomIn, zoomOut, zoomTo, fitView } = useReactFlow();
  const { zoom } = useViewport();
  const [open, setOpen] = useState(false);
  const btn: React.CSSProperties = {
    alignItems: "center", background: "transparent", border: "none", borderRadius: 8,
    color: "#b6c2d4", cursor: "pointer", display: "flex", fontSize: 13, fontWeight: 600,
    height: 40, justifyContent: "center", minWidth: 40, padding: "0 8px",
  };
  const item: React.CSSProperties = {
    background: "transparent", border: "none", borderRadius: 6, color: "#b6c2d4",
    cursor: "pointer", display: "block", fontSize: 12, padding: "7px 12px",
    textAlign: "left", width: "100%",
  };
  const pick = (fn: () => void) => () => { fn(); setOpen(false); };
  return (
    <div
      data-testid="studio-canvas-toolbar" style={{
        alignItems: "center", backdropFilter: "blur(40px)", background: "rgba(10,14,21,.78)",
        border: "1px solid #223046", borderRadius: 12, bottom: 8, boxSizing: "border-box",
        display: "flex", flexDirection: "row", height: 50, left: 8, padding: "0 4px",
        position: "absolute", zIndex: 7,
      }}
    >
      {/* Hand vs select — the hand is the DEFAULT: a left-drag anywhere on the
          board (background or card) pans (useBoardHand). Select mode restores
          rubber-band select on the background; middle-drag and Space+drag
          still pan there. */}
      <button
        type="button"
        style={{ ...btn, background: tool === "hand" ? "rgba(56,189,248,.18)" : "transparent", color: tool === "hand" ? "#e2e8f0" : "#b6c2d4" }}
        title={t("studio.canvas.handTool")}
        onClick={() => setTool("hand")}
      >✋</button>
      <button
        type="button"
        style={{ ...btn, background: tool === "select" ? "rgba(56,189,248,.18)" : "transparent", color: tool === "select" ? "#e2e8f0" : "#b6c2d4" }}
        title={t("studio.canvas.selectTool")}
        onClick={() => setTool("select")}
      >⬚</button>
      <div style={{ background: "#223046", height: 22, margin: "0 4px", width: 1 }} />
      <button type="button" style={{ ...btn, fontSize: 20 }} title={t("studio.canvas.zoomOut")} onClick={() => zoomOut({ duration: 160 })}>−</button>
      <div style={{ position: "relative" }}>
        <button
          type="button" data-testid="studio-zoom-readout" style={{ ...btn, gap: 4, width: 74 }}
          title={t("studio.canvas.zoom")} onClick={() => setOpen((v) => !v)}
        >
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{Math.round(zoom * 100)}%</span>
          <span style={{ fontSize: 11, opacity: .7 }}>▾</span>
        </button>
        {open && (
          <div style={{
            background: "rgba(10,14,21,.96)", border: "1px solid #223046", borderRadius: 10,
            bottom: 50, boxShadow: "0 10px 24px rgba(0,0,0,.45)", left: 0, minWidth: 130,
            padding: 4, position: "absolute",
          }}>
            <button type="button" style={item} onClick={pick(() => fitView({ padding: 0.15, duration: 250 }))}>{t("studio.canvas.zoomToFit")}</button>
            {[0.5, 1, 1.5, 2, 3, CANVAS_MAX_ZOOM].map((z) => (
              <button key={z} type="button" style={item} onClick={pick(() => zoomTo(z, { duration: 200 }))}>{z * 100}%</button>
            ))}
          </div>
        )}
      </div>
      <button type="button" style={{ ...btn, fontSize: 20 }} title={t("studio.canvas.zoomIn")} onClick={() => zoomIn({ duration: 160 })}>+</button>
    </div>
  );
}

/** The live board's last row (Approve all / Render) and the price question or
 *  refusal under it, kept in clear view. On a short laptop screen (1366x650,
 *  2026-10-05 walk) the board landed with "Approve all" under the zoom pill and
 *  the question below the canvas edge. When the board arrives, and whenever
 *  the question opens or a refusal prints, the board slides up just far
 *  enough. The zoom never changes, and a row already in view never moves. */
function useBoardActionsInView(live: StoryboardProject | null) {
  const { getViewport, setViewport } = useReactFlow();
  const runId = live?.runId || "";
  const scenes = live?.clips?.length || 0;
  const asking = !!live?.renderAsk;
  const notice = live?.renderNotice || "";
  useEffect(() => {
    if (!scenes) return;
    let frame = 0;
    let tries = 0;
    const later = () => { if (++tries < 180) frame = requestAnimationFrame(nudge); };
    function nudge() {
      const canvas = document.querySelector<HTMLElement>('[data-testid="studio-canvas"]');
      const render = document.querySelector<HTMLElement>('[data-testid="studio-render"]');
      if (!canvas || !render) return later();
      const target = (asking && document.querySelector<HTMLElement>('[data-testid="studio-render-confirm"]'))
        || (notice && document.querySelector<HTMLElement>('[data-testid="studio-render-notice"]'))
        || render.parentElement || render;
      const r = target.getBoundingClientRect();
      // The canvas keeps a card hidden while it measures it.
      if (!r.height) return later();
      const c = canvas.getBoundingClientRect();
      const pill = canvas.querySelector<HTMLElement>('[data-testid="studio-canvas-toolbar"]')?.getBoundingClientRect();
      const floor = (pill && pill.height ? pill.top : c.bottom) - 10;
      const below = r.bottom - floor;
      if (below <= 0) return;
      // A canvas too short for the whole question keeps its top in view (44 = the hint chip).
      const dy = Math.min(below, Math.max(0, r.top - (c.top + 44)));
      if (dy <= 0) return;
      const vp = getViewport();
      setViewport({ ...vp, y: vp.y - dy }, { duration: 200 });
    }
    frame = requestAnimationFrame(nudge);
    return () => cancelAnimationFrame(frame);
  }, [runId, scenes, asking, notice, getViewport, setViewport]);
}

function CanvasInner({ activeService }: { activeService: StudioService | null }) {
  const { t } = useTranslation();
  // The board mirrors ONLY the active tool (compass-driven, 2026-07-22). Each
  // studio-bus channel is service-tagged, so we filter every card to the tool
  // shown up top; nothing from another tool leaks onto this board.
  const liveRaw = useStoryboardProject();
  const briefRaw = useStoryBrief();
  const progressListRaw = useStudioProgress();
  const resultListRaw = useStudioResult();
  const reviewRaw = useClipReview();
  const adBriefRaw = useAdBrief();
  const stillsRaw = useStudioStills();
  const live = liveRaw && (!activeService || liveRaw.service === activeService) ? liveRaw : null;
  const brief = activeService === "story" ? briefRaw : null;      // brief is story-only
  const review = activeService === "corrido" ? reviewRaw : null;  // clip review is corrido-only
  const adBrief = activeService === "ads" ? adBriefRaw : null;    // ad brief is ads-only
  const stills = activeService === "ads" && stillsRaw ? stillsRaw : null; // image-ad grid is ads-only
  useBoardActionsInView(live);
  // MEMOIZED: .filter() makes a NEW array every render; without memo these feed
  // buildNodes -> setNodes -> re-render -> new arrays -> infinite loop (React
  // #185). Keyed on the raw refs (stable when unchanged) + activeService.
  const progressList = useMemo(() => progressListRaw.filter((p) => p.service === activeService), [progressListRaw, activeService]);
  const resultList = useMemo(() => resultListRaw.filter((r) => r.service === activeService), [resultListRaw, activeService]);
  const archivedRef = useRef<Map<string, StoryboardProject>>(new Map());
  const lastLiveRef = useRef<StoryboardProject | null>(null);
  /** Job ids the chat has shown live on this board (see buildNodes). */
  const seenLiveJobsRef = useRef<Set<string>>(new Set());
  // Hand is the default navigation tool (see CanvasToolbar and boardHand).
  const [tool, setTool] = useState<"hand" | "select">("hand");
  const boardRef = useRef<HTMLDivElement | null>(null);
  useBoardHand(boardRef, { tool, minZoom: CANVAS_MIN_ZOOM, maxZoom: CANVAS_MAX_ZOOM });
  // UNFINISHED projects (previously a ChopShop panel) live on the board as
  // background storyboards: anything in flight or awaiting Stage-2 review.
  // Corrido-only (the /runs endpoint is the corrido pipeline's).
  const [unfinished, setUnfinished] = useState<UnfinishedRun[]>([]);
  const deleteRun = useCallback(async (jobId: string) => {
    setUnfinished((prev) => prev.filter((r) => r.jobId !== jobId)); // optimistic — off the board immediately
    const token = localStorage.getItem("auth_token");
    try {
      await fetch(`${corridoApi("/run/")}${encodeURIComponent(jobId)}`, {
        method: "DELETE", headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch { /* already removed from the board; server cleanup is best-effort */ }
  }, []);
  // A live card that sees its job end asks for a fresh /runs, so the card turns
  // into the finished storyboard (or clips) it produced.
  const [runsNonce, setRunsNonce] = useState(0);
  const refreshRuns = useCallback(() => setRunsNonce((n) => n + 1), []);
  useEffect(() => {
    if (activeService !== "corrido") { setUnfinished([]); return; }
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    const auth = { Authorization: `Bearer ${token}` };
    fetch(corridoApi("/runs"), { headers: auth })
      .then((r) => (r.ok ? r.json() : { runs: [] }))
      .then(async (j) => {
        const runs: UnfinishedRun[] = Array.isArray(j.runs) ? j.runs : [];
        // EVERY reopenable run gets a card (field incident 2026-07-22: past
        // storyboards were invisible): in-flight work, storyboards awaiting
        // approval, clips awaiting review, and interrupted/failed runs (their
        // checkpoints survive on disk — "failed" usually means power/restart).
        // Only delivered finals are excluded; those live in the gallery.
        const seen = new Set<string>();
        const keep = runs.filter((r) => {
          const reopen = isRunInFlight(r.status)
            || (r.type === "corrido_storyboard" && r.status === "completed" && r.run_id)
            || (r.type === "corrido_clips" && r.status === "completed" && r.run_id)
            || (r.status === "failed" && !!r.run_id);
          if (!reopen) return false;
          const key = r.run_id || r.jobId;      // one card per run, newest wins
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setUnfinished(keep);
        // Best-effort: pull each run's stills so the card renders as a FULL
        // storyboard strip, not a bare chip. Clips first; a run that never
        // reached animation falls back to its storyboard stills.
        const withRun = keep.filter((r) => r.run_id).slice(0, 6);
        await Promise.all(withRun.map(async (r) => {
          try {
            let thumbs: string[] = [];
            const res = await fetch(`${corridoApi("/clips")}?run_id=${encodeURIComponent(r.run_id!)}`, { headers: auth });
            if (res.ok) {
              const cj = await res.json();
              thumbs = (cj.clips || []).map((c: { image?: string | null }) => c.image).filter(Boolean) as string[];
            }
            if (!thumbs.length) {
              const sres = await fetch(`${corridoApi("/storyboard_state")}?run_id=${encodeURIComponent(r.run_id!)}`, { headers: auth });
              if (sres.ok) {
                const sj = await sres.json();
                thumbs = (sj.storyboard || []).map((c: { image?: string | null }) => c.image).filter(Boolean) as string[];
              }
            }
            if (thumbs.length) setUnfinished((prev) => prev.map((p) => (p.jobId === r.jobId ? { ...p, thumbs } : p)));
          } catch { /* card still works without thumbnails */ }
        }));
      })
      .catch(() => { /* board works without it */ });
  }, [activeService, runsNonce]);

  // When a NEW run replaces the current one, keep the old board as an archived
  // node (up to 3) instead of vanishing it.
  useEffect(() => {
    const prev = lastLiveRef.current;
    if (prev?.runId && live?.runId && prev.runId !== live.runId) {
      // A snapshot of a board that had the price question up must not keep it.
      archivedRef.current.set(prev.runId, { ...prev, renderAsk: false, renderNotice: null });
      while (archivedRef.current.size > 3) {
        const oldest = archivedRef.current.keys().next().value;
        if (oldest === undefined) break;
        archivedRef.current.delete(oldest);
      }
    }
    if (live) lastLiveRef.current = live;
  }, [live]);

  const nodeTypes = useMemo(() => ({
    brief: BriefNode, adbrief: AdBriefNode, stills: StillsNode, project: ProjectNode, progress: ProgressNode, player: PlayerNode, resume: ResumeNode, review: ClipsReviewNode,
  }), []);

  const archivedKey = [...archivedRef.current.keys()].join(",");
  const hasNodes = !!brief || !!adBrief || !!stills || !!live || !!review || progressList.length > 0 || resultList.length > 0 || archivedRef.current.size > 0 || unfinished.length > 0;

  const buildNodes = useCallback((): Node[] => {
    const out: Node[] = [];
    const archived = [...archivedRef.current.values()]
      .filter((p) => p.runId !== live?.runId)
      .filter((p) => !activeService || p.service === activeService);
    let x = 40;
    if (brief) {
      out.push({ id: "story-brief", type: "brief", position: { x, y: 40 }, data: { brief } });
      x += NODE_W + NODE_GAP;
    }
    if (adBrief) {
      out.push({ id: "ad-brief", type: "adbrief", position: { x, y: 40 }, data: { brief: adBrief } });
      x += NODE_W + NODE_GAP;
    }
    if (live) {
      out.push({
        id: `run:${live.runId || "current"}`,
        type: "project",
        position: { x, y: 40 },
        data: {
          project: live,
          dimmed: false,
          title: t("studio.canvas.liveTitle", { service: live.service, name: live.songName || t("studio.canvas.currentProject") }),
        },
      });
      x += NODE_W * 2 + NODE_GAP;
    }
    if (review) {
      out.push({ id: `review:${review.runId || "current"}`, type: "review", position: { x, y: 40 }, data: { review } });
      x += Math.round(NODE_W * 1.4) + NODE_GAP;
    }
    progressList.forEach((progress) => {
      out.push({ id: `progress:${progress.service}`, type: "progress", position: { x, y: 40 }, data: { progress } });
      x += NODE_W + NODE_GAP;
    });
    resultList.forEach((result) => {
      out.push({ id: `player:${result.service}`, type: "player", position: { x, y: 40 }, data: { result } });
      x += NODE_W + NODE_GAP;
    });
    if (stills) {
      out.push({ id: "stills:ads", type: "stills", position: { x, y: 40 }, data: { stills } });
      x += NODE_W + NODE_GAP;
    }
    archived.forEach((project) => {
      out.push({
        id: `run:${project.runId}`,
        type: "project",
        position: { x, y: 40 },
        data: {
          project,
          dimmed: true,
          title: t("studio.canvas.archivedSuffix", { service: project.service, name: project.songName || project.runId.slice(0, 8) }),
        },
      });
      x += NODE_W * 2 + NODE_GAP;
    });
    // Past / unfinished projects sit in the SAME main row as everything else, at
    // full size — CapCut-style multiple selectable storyboards, not minimized
    // chips parked at the bottom (user directive 2026-07-21).
    // The builds the chat is showing live right now — the storyboard being
    // drawn, the loading card (clips, cut), a new take — need no second card
    // polling the same job beside them. A job once shown live stays the chat's
    // while this board's /runs list still calls it in flight: when it finishes,
    // the chat's own board replaces it, instead of a stale "still building"
    // card popping up for the moment it takes to notice.
    const liveJobs = [live?.build?.jobId, review?.build?.jobId, ...progressList.map((p) => p.build?.jobId)]
      .filter((id): id is string => !!id);
    liveJobs.forEach((id) => seenLiveJobsRef.current.add(id));
    unfinished
      .filter((r) => !(live?.runId && r.run_id === live.runId))
      .filter((r) => !liveJobs.includes(r.jobId))
      .filter((r) => !(isRunInFlight(r.status) && seenLiveJobsRef.current.has(r.jobId)))
      .forEach((run) => {
        out.push({ id: `resume:${run.jobId}`, type: "resume", position: { x, y: 40 }, data: { run, onDelete: deleteRun, onRefresh: refreshRuns } });
        x += NODE_W + NODE_GAP;
      });
    // A card moves from its grip bar only; anywhere else on it moves the board.
    return out.map((n) => ({ ...n, dragHandle: GRIP_SELECTOR }));
    // archivedKey tracks the archive map, which isn't reactive state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brief, adBrief, stills, live, review, progressList, resultList, archivedKey, unfinished, activeService, deleteRun, refreshRuns, t]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(buildNodes());

  // Rebuild when the boards change, but KEEP any position the user dragged a
  // node to — moving a card and having it snap back is maddening.
  useEffect(() => {
    setNodes((prev) => {
      const placed = new Map(prev.map((n) => [n.id, n.position]));
      return buildNodes().map((n) => ({ ...n, position: placed.get(n.id) ?? n.position }));
    });
  }, [buildNodes, setNodes]);

  return (
    // overflow:hidden (2026-09-24): on a phone the board is a 150-260px strip
    // and the empty-state card used to spill out of it over the chat, hiding
    // the "Sign up / Log in" button. data-empty lets the console's phone CSS
    // tidy the empty board (DirectorConsole's EMPTY BOARD block, phones and
    // short landscape screens); nothing reads it.
    <div
      ref={boardRef} className="studio-board" data-testid="studio-canvas" data-tool={tool} data-empty={hasNodes ? undefined : "true"}
      style={{ height: "100%", overflow: "hidden", position: "relative", width: "100%" }}
    >
      <style>{BOARD_CSS}</style>
      {hasNodes && (
        <div data-testid="studio-canvas-hint" style={{ alignItems: "center", background: "rgba(5,7,11,.85)", border: "1px solid #223046", borderRadius: 999, boxSizing: "border-box", color: "#94a3b8", display: "inline-flex", fontSize: 12, gap: 6, left: 8, maxWidth: "calc(100% - 16px)", padding: "4px 10px", pointerEvents: "none", position: "absolute", top: 8, zIndex: 6 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t("studio.canvas.boardHint")}</span>
        </div>
      )}
      {!hasNodes && <EmptyState activeService={activeService} />}
      {/* Phone: the same board and review as a full-width list (the 260px strip
          cannot host a 36-scene approval). Renders nothing on wider screens. */}
      <MobileReviewSheet live={live} review={review} />
      <ReactFlow
        nodes={nodes}
        onNodesChange={onNodesChange}
        nodeTypes={nodeTypes}
        // Cards are a work surface, not a graph: no edges, nothing to connect.
        edges={[]}
        nodesConnectable={false}
        proOptions={{ hideAttribution: true }}
        minZoom={CANVAS_MIN_ZOOM}
        maxZoom={CANVAS_MAX_ZOOM}
        defaultViewport={{ x: 0, y: 0, zoom: 0.85 }}
        panOnScroll
        // HAND (default): the board's drag is useBoardHand's, from the
        // background AND the cards (React Flow's own pans only from the bare
        // background). SELECT: left-drag rubber-band selects; right-drag pans
        // here, middle-drag and Space+drag pan in useBoardHand.
        selectionOnDrag={tool === "select"}
        panOnDrag={tool === "hand" ? false : [1, 2]}
        selectionKeyCode={tool === "select" ? "Shift" : null}
        panActivationKeyCode={null}
        // A selected card must not vanish on Backspace: cards come from the run.
        deleteKeyCode={null}
        style={{ background: "transparent", cursor: tool === "hand" ? "grab" : "default" }}
      >
        <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="rgba(255,255,255,.10)" />
      </ReactFlow>
      <CanvasToolbar tool={tool} setTool={setTool} />
    </div>
  );
}

/** `hint` is no longer shown: the board says how it moves itself
 *  (studio.canvas.boardHint), since "Drag a card to move it" stopped being true. */
export default function StoryboardCanvas({ activeService = "corrido" }: { hint?: string; activeService?: StudioService | null }) {
  // A look switch (lib/corrido-api.ts) re-opens the board on the other product's runs.
  const look = useCorridoLook();
  return (
    <ReactFlowProvider key={look}>
      <CanvasInner activeService={activeService} />
    </ReactFlowProvider>
  );
}
